import React, { Component } from "react";
import { connect } from "react-redux";

import * as localforage from "localforage";
import EJSON from "ejson";

import { TTP_TALK_URL, APP_ENV } from "Config";

import { Rocketchat } from "@rocket.chat/sdk";
import { mergeSubscriptionsRooms, merge } from "./RocketChat";

import TalkComponent from "./TalkComponent";

const withCatch = fn => (...params) => {
  try {
    fn(...params);
  } catch (e) {
    console.error(e);
  }
};

const CACHE_VERSION = 1; // inc this to reset cache
const TALK_LOGIN_DATA = `TALK_LOGIN_TOKEN_${APP_ENV}`;
const TALK_SUBSCRIPTIONS = `TALK_SUBSCRIPTIONS_${APP_ENV}`;
const STATUS_MAP = ["offline", "online", "away", "busy"];

const tokenExpiresSoon = function(when) {
  var minLifetimeMs = 3600 * 1000; // token will expire after one Hour ?
  return new Date() > new Date(when) - minLifetimeMs;
};

const saveCache = ({ key, data, ttpUserId }) => {
  return localforage
    .setItem(key, {
      CACHE_VERSION,
      TTP_USER_ID: ttpUserId,
      DATA: EJSON.fromJSONValue(data)
    })
    .catch(() => {});
};

const getFromCache = ({ key, ttpUserId }) => {
  return localforage
    .getItem(key)
    .then(item => {
      if (
        item &&
        item.CACHE_VERSION === CACHE_VERSION &&
        item.TTP_USER_ID === ttpUserId
      ) {
        return item.DATA;
      }

      localforage.removeItem(key);
    })
    .catch(() => {});
};

const isTalkActivated = props => {
  const { auth } = props;

  if (!auth) {
    return false;
  }

  const { navCommunity, user, loggedAs } = auth;
  if (!navCommunity || !user || !loggedAs) {
    return false;
  }

  if (+navCommunity.chatActivated !== 1) {
    // organization/fiduciaire activé ?
    return false;
  }

  const { userSettings = {}, roles = [], role } = user;
  const organizationId = navCommunity.id;

  if (!roles.length === 0) {
    // Cas d'un simple membre d'une comm
    return (
      navCommunity.talkPreferences &&
      +navCommunity.talkPreferences.communityChannelActivated === 1
    );
  }

  if (
    loggedAs === "CLIENT" &&
    role &&
    role.type === "LEGAL_REPRESENTATIVE" &&
    role.typeStatus === "FOLDER"
  ) {
    // Client activé ?
    return role.organization && +role.organization.chatActivated === 1;
  }

  const { talkSettings = {} } = userSettings;
  if (
    talkSettings[organizationId] &&
    +talkSettings[organizationId].chatActivated === 1
  ) {
    // Employé activé ?
    return true;
  }

  return false;
};

class TalkContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      visible: false,
      loading: false,
      error: false,
      subscriptions: [],
      usersPresence: {},
      connectionState: "CONNECTING"
    };
  }

  static getDerivedStateFromProps(props, state) {
    const visible = isTalkActivated(props);

    return {
      visible
    };
  }

  componentDidMount() {
    if (this.state.visible) {
      this.start();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { visible } = this.state;

    if (!prevState.visible && visible) {
      this.start();
    }

    if (prevState.visible && !visible) {
      this.stop();
    }
  }

  componentWillUnmount() {
    this.stop();
  }

  connect = () => {
    this.stop();
    this.setState({ connectionState: "CONNECTING" });

    this.sdk = new Rocketchat({
      host: TTP_TALK_URL,
      protocol: "ddp",
      useSsl: !/http:\/\//.test(TTP_TALK_URL)
      // logger: console
    });

    this.connectedListener = this.sdk.onStreamData("connected", () => {
      this.setState({ connectionState: "CONNECTED" });

      clearTimeout(this.reconnectTimer);
      if (this.sdk.userId) {
        // if userId was present, this means the sdk is trying a reconnection
        this.start({ loading: false });
      }
    });

    this.disconnectedListener = this.sdk.onStreamData("close", e => {
      // try to detect if the login token expired
      if (e.code === 1000 && e.wasClean && this.sdk.userId) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => {
          this.start({ loading: false });
        }, 1000 * 10);
      }

      this.setState({ connectionState: "DISCONNECTED" });
    });

    return this.sdk.connect();
  };

  login() {
    const { auth } = this.props;

    const token = auth.token;
    const id = auth.user.id;

    // Auto login with TTP Token which creates a new Meteor token
    const loginWithTTPToken = () =>
      this.sdk
        .methodCall("loginTTPUser", { token, id })
        .then(({ token }) => this.sdk.login({ resume: token }))
        .then(loginData => {
          saveCache({ key: TALK_LOGIN_DATA, data: loginData, ttpUserId: id });
          return loginData;
        });

    return this.connect().then(() => {
      return (
        getFromCache({ key: TALK_LOGIN_DATA, ttpUserId: id })
          .then(loginData => {
            // in case of no data or Meteor token expires soon, auto login with TTP Token which creates a new Meteor token
            if (
              !loginData ||
              !loginData.token ||
              !loginData.tokenExpires ||
              tokenExpiresSoon(loginData.tokenExpires)
            ) {
              return loginWithTTPToken();
            }

            // try login with cached Meteor token, if it fails auto login with TTP Token which creates a new Meteor token
            return this.sdk
              .login({ resume: loginData.token })
              .catch(() => loginWithTTPToken());
          })
          // in case of error accessing the cache, auto login with TTP Token which creates a new Meteor token
          .catch(() => loginWithTTPToken())
      );
    });
  }

  handleUsersPresence = () => {
    const usersRequestId = `USERS_REQUEST_ID_${performance.now()}`;
    this.currentUsersRequestId = usersRequestId;

    // Get data from server
    this.sdk.get("users.presence", {}).then(result => {
      if (!result.success) {
        return;
      }

      if (usersRequestId !== this.currentUsersRequestId) {
        // To avoid race condition
        return;
      }

      const usersPresence = result.users.reduce((ret, item) => {
        ret[item.username] = item.status;
        return ret;
      }, {});

      this.setState({ usersPresence });
    });

    // Subscribe to stream
    const stream = "stream-notify-logged";
    this.sdk.subscribe(stream, "user-status");

    this.usersPresenceListener = this.sdk.onStreamData(
      stream,
      withCatch(ddpMessage => {
        const { eventName } = ddpMessage.fields;

        if (eventName !== "user-status") {
          return;
        }

        const userStatus = ddpMessage.fields.args[0];
        const [, username, status] = userStatus;

        this.setState(({ usersPresence }) => ({
          usersPresence: {
            ...usersPresence,
            [username]: STATUS_MAP[status]
          }
        }));
      })
    );
  };

  handleSubscriptions = () => {
    const ttpUserId = this.props.auth.user.id;
    const subsRequestId = `SUBS_REQUEST_ID_${performance.now()}`;
    this.currentSubsRequestId = subsRequestId;
    this.subsLoadedFromServer = false;

    // Get subscriptions from cache, for quick ui feedback
    getFromCache({ key: TALK_SUBSCRIPTIONS, ttpUserId }).then(subscriptions => {
      if (subscriptions && !this.subsLoadedFromServer) {
        this.setState({ subscriptions, loading: false });
      }
    });

    // Get data from server
    Promise.all([
      this.sdk.methodCall("subscriptions/get"),
      this.sdk.methodCall("rooms/get")
    ])
      .then(([subscriptionsResult, roomsResult]) => {
        if (subsRequestId !== this.currentSubsRequestId) {
          // To avoid race condition
          return;
        }

        this.subsLoadedFromServer = true;
        const { subscriptions } = mergeSubscriptionsRooms(
          subscriptionsResult,
          roomsResult
        );
        this.setState({ subscriptions, loading: false }, () =>
          saveCache({
            key: TALK_SUBSCRIPTIONS,
            data: this.state.subscriptions,
            ttpUserId
          })
        );
      })
      .catch(error => {
        this.setState({ error: true, loading: false });
      });

    // Subscribe to stream
    const stream = "stream-notify-user";
    this.sdk.subscribeNotifyUser();

    this.subscriptionsListener = this.sdk.onStreamData(
      stream,
      withCatch(ddpMessage => {
        if (ddpMessage.msg === "added") {
          return;
        }

        const [type, data] = ddpMessage.fields.args;
        const [, ev] = ddpMessage.fields.eventName.split("/");

        if (/subscriptions/.test(ev)) {
          if (type === "removed") {
            return this.setState(
              ({ subscriptions }) => ({
                subscriptions: subscriptions.filter(sub => sub._id !== data._id)
              }),
              () =>
                saveCache({
                  key: TALK_SUBSCRIPTIONS,
                  data: this.state.subscriptions,
                  ttpUserId
                })
            );
          }

          const subToUpdate = this.state.subscriptions.find(
            sub => sub._id === data._id
          );

          // TODO update the cache !!
          if (!subToUpdate) {
            return this.setState(
              ({ subscriptions }) => ({
                subscriptions: subscriptions.concat(EJSON.fromJSONValue(data))
              }),
              () =>
                saveCache({
                  key: TALK_SUBSCRIPTIONS,
                  data: this.state.subscriptions,
                  ttpUserId
                })
            );
          }

          this.setState(
            ({ subscriptions }) => {
              const newSubscriptions = subscriptions.map(sub => {
                if (sub._id === data._id) {
                  return {
                    ...sub,
                    ...EJSON.fromJSONValue(data)
                  };
                }
                return sub;
              });

              return { subscriptions: newSubscriptions };
            },
            () =>
              saveCache({
                key: TALK_SUBSCRIPTIONS,
                data: this.state.subscriptions,
                ttpUserId
              })
          );
        }

        if (/rooms/.test(ev) && (type === "updated" || type === "inserted")) {
          const subToUpdate = this.state.subscriptions.find(
            sub => sub.rid === data._id
          );

          if (!subToUpdate) {
            return;
          }

          const newSubData = merge(subToUpdate, data);
          this.setState(
            ({ subscriptions }) => {
              const newSubscriptions = subscriptions.map(sub => {
                if (sub._id === subToUpdate._id) {
                  return newSubData;
                }
                return sub;
              });

              return { subscriptions: newSubscriptions };
            },
            () =>
              saveCache({
                key: TALK_SUBSCRIPTIONS,
                data: this.state.subscriptions,
                ttpUserId
              })
          );
        }
      })
    );
  };

  start = ({ loading = true } = {}) => {
    this.stop();
    this.setState({ loading, error: false });

    this.login()
      .then(result => {
        this.handleSubscriptions();
        this.handleUsersPresence();
      })
      .catch(error => {
        this.setState({ loading: false, error: true });
      });
  };

  stopListeners() {
    clearTimeout(this.reconnectTimer);

    const listeners = [
      this.connectedListener,
      this.disconnectedListener,
      this.usersPresenceListener,
      this.subscriptionsListener
    ];

    listeners.forEach(listener => {
      listener && listener.then(listener => listener && listener.stop());
    });
  }

  stop = () => {
    this.stopListeners();
    this.sdk && this.sdk.disconnect();
    this.sdk = null;
  };

  canViewSub = ({ sub, organizationId }) => {
    // hidden room
    if (!sub.open) {
      return false;
    }

    // public room
    if (sub.t === "c") {
      return true;
    }

    // a discussion from a public room
    if (sub.prid && !sub.ttpAllowedOrganizations) {
      return true;
    }

    const { ttpAllowedOrganizations = [] } = sub;
    return ttpAllowedOrganizations.find(o => o.id === organizationId);
  };

  render() {
    const { auth } = this.props;
    const {
      visible,
      loading,
      error,
      subscriptions,
      usersPresence,
      connectionState
    } = this.state;

    if (!visible) {
      return null;
    }

    const { user, navCommunity, loggedAs } = auth;
    const { role } = user;

    let organizationId = navCommunity.id;
    if (loggedAs === "CLIENT") {
      organizationId = role.organization.id;
    }

    const openedSubscriptions = subscriptions
      .filter(sub => this.canViewSub({ sub, organizationId }))
      .sort(
        (a, b) =>
          new Date(b._updatedAt).getTime() - new Date(a._updatedAt).getTime()
      );

    return (
      <div className="cell medium-6 large-3">
        <TalkComponent
          loading={loading}
          error={error}
          onReload={this.start}
          subscriptions={openedSubscriptions}
          usersPresence={usersPresence}
          connectionState={connectionState}
        />
      </div>
    );
  }
}
// Connected Component
const mapStateToProps = state => ({
  auth: state.auth
});

const mapDispatchToProps = dispatch => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TalkContainer);



// WEBPACK FOOTER //
// ./src/components/Community/ClientSpace/Talk/TalkContainer.js